type Edge = "top" | "bottom";

const reserved: Record<Edge, Map<HTMLElement, number>> = {
  top: new Map(),
  bottom: new Map(),
};
const callbacks = new Map<string, (bar: HTMLElement | null) => () => void>();
let focusedBar: HTMLElement | null = null;

function apply(edge: Edge) {
  const root = document.documentElement;
  // A focused control inside a bar is always on screen, but with the bar's
  // space reserved the browser scrolls the page on every keystroke trying to
  // reveal its caret.
  if (!reserved[edge].size || focusedBar) {
    root.style.removeProperty(`scroll-padding-${edge}`);
    return;
  }
  root.style.setProperty(
    `scroll-padding-${edge}`,
    `${Math.max(...reserved[edge].values())}px`,
  );
}

/**
 * Ref callback for a sticky bar `offset` px from the given edge of the page.
 * Reserves the bar's height plus `offset` as scroll padding, so focused and
 * scrolled-to controls stay clear of it.
 */
export function reserveScrollPadding(edge: Edge, offset: number) {
  const key = `${edge}:${offset}`;
  let callback = callbacks.get(key);
  if (!callback) {
    callback = (bar) => {
      if (!bar) return () => {};
      const update = () => {
        reserved[edge].set(bar, bar.offsetHeight + offset);
        apply(edge);
      };
      const onFocusIn = () => {
        focusedBar = bar;
        apply(edge);
      };
      const onFocusOut = (event: FocusEvent) => {
        if (bar.contains(event.relatedTarget as Node | null)) return;
        if (focusedBar === bar) focusedBar = null;
        apply(edge);
      };
      update();
      const observer = new ResizeObserver(update);
      observer.observe(bar);
      bar.addEventListener("focusin", onFocusIn);
      bar.addEventListener("focusout", onFocusOut);
      return () => {
        observer.disconnect();
        bar.removeEventListener("focusin", onFocusIn);
        bar.removeEventListener("focusout", onFocusOut);
        if (focusedBar === bar) focusedBar = null;
        reserved[edge].delete(bar);
        apply(edge);
      };
    };
    callbacks.set(key, callback);
  }
  return callback;
}
